import React, { useEffect, useState } from 'react';
import { Typography, Paper, IconButton, Link, Collapse } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Utils } from '../../utils/utils';

export default function CustomerReadiness({ user }) {
  const [expandedChecks, setExpandedChecks] = useState({});

  const toggleCheckDetails = (checkId) => {
    setExpandedChecks((prev) => ({
      ...prev,
      [checkId]: !prev[checkId],
    }));
  };

  const checks = [
    {
      id: 'customer-info',
      label: 'Complete Customer Information',
      href: '/profile',
      tooltip: 'Complete First and Last Name, Email, Phone Number, and Profile Picture',
      completed:
        user?.firstName &&
        user?.lastName &&
        user?.email &&
        user?.phoneNumber &&
        user?.profilePic,
    },
    {
      id: 'customer-acknowledgment',
      label: 'Complete Acknowledgment',
      href: '/profile#compliance-acknowledgment-section',
      tooltip: 'You need to acknowledge that you are 18+ and responsible about how you use this site.',
      completed: user?.customer?.complianceAcknowledgment,
    },
  ];

  const completedCount = checks.filter((check) => check.completed).length;
  const totalChecks = checks.length;
  const allComplete = completedCount === totalChecks && Utils.isCustomerReady(user);
  const [isPanelExpanded, setIsPanelExpanded] = useState(!allComplete);

  useEffect(() => {
    setIsPanelExpanded(!allComplete);
  }, [allComplete]);

  return (
    <Paper
      elevation={2}
      style={{
        padding: 16,
        marginTop: 16,
        marginBottom: 16,
        backgroundColor: allComplete ? '#e8f5e9' : '#fff3e0',
      }}
      data-testid="customer-readiness-panel"
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Typography
            variant="h6"
            style={{ color: allComplete ? '#2e7d32' : 'inherit' }}
          >
            {allComplete ? 'You are all set' : 'Get Ready to Order!'}
          </Typography>

          {isPanelExpanded && (
            <Typography variant="caption" color="text.secondary" style={{ display: 'block' }}>
              {completedCount} of {totalChecks} completed
            </Typography>
          )}
        </div>

        <IconButton
          size="small"
          onClick={() => setIsPanelExpanded((prev) => !prev)}
          aria-label={isPanelExpanded ? 'Collapse customer readiness panel' : 'Expand customer readiness panel'}
          aria-expanded={isPanelExpanded}
          data-testid="customer-readiness-toggle"
        >
          <ExpandMoreIcon
            style={{
              transform: isPanelExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 120ms ease-in-out',
            }}
          />
        </IconButton>
      </div>

      <Collapse in={isPanelExpanded} timeout="auto" unmountOnExit>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }} data-testid="customer-readiness-content">
          {checks.map((check) => (
            <div
              key={check.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
              data-testid={`check-${check.id}`}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {check.completed ? (
                  <CheckCircleIcon style={{ color: '#4caf50', fontSize: 20 }} data-testid={`icon-${check.id}-completed`} />
                ) : (
                  <RadioButtonUncheckedIcon style={{ color: '#9e9e9e', fontSize: 20 }} data-testid={`icon-${check.id}-incomplete`} />
                )}
                {check.completed ? (
                  <Typography
                    component="span"
                    data-testid={`label-${check.id}`}
                    sx={{
                      display: 'block',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      color: 'success.dark',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      lineHeight: 1.4,
                      flex: 1,
                    }}
                  >
                    {check.label}
                  </Typography>
                ) : (
                  <Link
                    href={check.href}
                    underline="none"
                    data-testid={`link-${check.id}`}
                    sx={{
                      display: 'block',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      lineHeight: 1.4,
                      flex: 1,
                      transition: 'background-color 120ms ease-in-out',
                      '&:hover': {
                        textDecoration: 'none',
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    {check.label}
                  </Link>
                )}
                <IconButton
                  size="small"
                  style={{ padding: 4 }}
                  onClick={() => toggleCheckDetails(check.id)}
                  aria-label={`Toggle details for ${check.label}`}
                  aria-expanded={Boolean(expandedChecks[check.id])}
                  data-testid={`help-${check.id}`}
                >
                  <ExpandMoreIcon
                    style={{
                      fontSize: 22,
                      color: '#757575',
                      transform: expandedChecks[check.id] ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 120ms ease-in-out',
                    }}
                  />
                </IconButton>
              </div>
              <Collapse in={Boolean(expandedChecks[check.id])} timeout="auto" unmountOnExit>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  style={{ display: 'block', marginLeft: 28, marginRight: 4, marginTop: 2 }}
                  data-testid={`details-${check.id}`}
                >
                  {check.tooltip}
                </Typography>
              </Collapse>
            </div>
          ))}
        </div>

        {allComplete && (
          <Typography
            variant="caption"
            style={{
              display: 'block',
              marginTop: 12,
              color: '#2e7d32',
              fontWeight: 500,
            }}
          >
            ✓ Your profile is now ready!
          </Typography>
        )}
      </Collapse>
    </Paper>
  );
}
